import React, { useState, useEffect, useRef, useMemo } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { blogAPI } from '../services/api';
import { format } from 'date-fns';

const BlogPosts = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEditor, setShowEditor] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [saving, setSaving] = useState(false);
  const quillRef = useRef(null);

  const [form, setForm] = useState({
    title: '',
    excerpt: '',
    content: '',
    featured_image: '',
    category: '',
    read_time: '',
    keywords: [],
    status: 'draft'
  });
  const [keywordInput, setKeywordInput] = useState('');

  const fetchPosts = async () => {
    try {
      const res = await blogAPI.list();
      setPosts(res.data.results || res.data);
    } catch (err) {
      console.error('Failed to fetch posts:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchPosts(); }, []);

  // Custom image handler for Quill
  const imageHandler = () => {
    const input = document.createElement('input');
    input.setAttribute('type', 'file');
    input.setAttribute('accept', 'image/*,video/*');
    input.click();

    input.onchange = async () => {
      const file = input.files[0];
      if (!file) return;

      try {
        const formData = new FormData();
        formData.append('file', file);
        const res = await blogAPI.uploadMedia(formData);
        const url = res.data.url;

        const quill = quillRef.current?.getEditor();
        if (quill) {
          const range = quill.getSelection(true);
          if (file.type.startsWith('video/')) {
            quill.insertEmbed(range.index, 'video', url);
          } else {
            quill.insertEmbed(range.index, 'image', url);
          }
          quill.setSelection(range.index + 1);
        }
      } catch (err) {
        alert('Failed to upload: ' + (err.response?.data?.error || err.message));
      }
    };
  };

  const modules = useMemo(() => ({
    toolbar: {
      container: [
        [{ 'header': [1, 2, 3, false] }],
        ['bold', 'italic', 'underline', 'strike'],
        [{ 'color': [] }, { 'background': [] }],
        [{ 'list': 'ordered' }, { 'list': 'bullet' }],
        [{ 'align': [] }],
        ['blockquote', 'code-block'],
        ['link', 'image', 'video'],
        ['clean']
      ],
      handlers: {
        image: imageHandler
      }
    }
  }), []);

  const formats = [
    'header', 'bold', 'italic', 'underline', 'strike',
    'color', 'background', 'list', 'bullet', 'align',
    'blockquote', 'code-block', 'link', 'image', 'video'
  ];

  const resetForm = () => {
    setForm({ title: '', excerpt: '', content: '', featured_image: '', category: '', read_time: '', keywords: [], status: 'draft' });
    setKeywordInput('');
    setEditingPost(null);
  };

  const openEditor = (post = null) => {
    if (post) {
      setEditingPost(post);
      setForm({
        title: post.title || '',
        excerpt: post.excerpt || '',
        content: post.content || '',
        featured_image: post.featured_image || '',
        category: post.category || '',
        read_time: post.read_time || '',
        keywords: post.keywords || [],
        status: post.status || 'draft'
      });
    } else {
      resetForm();
    }
    setShowEditor(true);
  };

  const handleSave = async () => {
    if (!form.title.trim() || !form.content.trim()) {
      alert('Title and content are required.');
      return;
    }
    setSaving(true);
    try {
      if (editingPost) {
        await blogAPI.update(editingPost.id, form);
      } else {
        await blogAPI.create(form);
      }
      setShowEditor(false);
      resetForm();
      fetchPosts();
    } catch (err) {
      alert('Failed to save: ' + (err.response?.data?.detail || err.message));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this post?')) return;
    try {
      await blogAPI.delete(id);
      fetchPosts();
    } catch (err) {
      alert('Failed to delete: ' + err.message);
    }
  };

  const handleFeaturedImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await blogAPI.uploadMedia(formData);
      setForm(f => ({ ...f, featured_image: res.data.url }));
    } catch (err) {
      alert('Failed to upload: ' + (err.response?.data?.error || err.message));
    }
  };

  const addKeyword = () => {
    const kw = keywordInput.trim();
    if (kw && !form.keywords.includes(kw)) {
      setForm(f => ({ ...f, keywords: [...f.keywords, kw] }));
      setKeywordInput('');
    }
  };

  const removeKeyword = (kw) => {
    setForm(f => ({ ...f, keywords: f.keywords.filter(k => k !== kw) }));
  };

  if (loading) {
    return <div style={{ display: 'flex', justifyContent: 'center', padding: '3rem' }}><div className="spinner"></div></div>;
  }

  // Editor view
  if (showEditor) {
    return (
      <div className="page-container">
        <div className="page-header" style={{ marginBottom: '1.5rem' }}>
          <div>
            <h1>{editingPost ? 'Edit Post' : 'New Post'}</h1>
          </div>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button className="btn btn-outline" onClick={() => { setShowEditor(false); resetForm(); }}>Cancel</button>
            <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
              {saving ? 'Saving...' : 'Save Post'}
            </button>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 320px', gap: '1.5rem' }}>
          {/* Main editor */}
          <div>
            <div className="card" style={{ padding: '1.5rem', marginBottom: '1rem' }}>
              <input
                type="text"
                placeholder="Post title..."
                value={form.title}
                onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
                style={{ width: '100%', fontSize: '1.5rem', fontWeight: '700', border: 'none', outline: 'none', padding: '0.5rem 0', marginBottom: '1rem', color: '#111827' }}
              />
              <textarea
                placeholder="Short excerpt/summary..."
                value={form.excerpt}
                onChange={e => setForm(f => ({ ...f, excerpt: e.target.value }))}
                rows={2}
                style={{ width: '100%', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '0.75rem', resize: 'vertical', fontSize: '0.95rem', marginBottom: '1rem' }}
              />
            </div>

            <div className="card" style={{ padding: '1.5rem' }}>
              <label style={{ fontWeight: '600', fontSize: '0.9rem', color: '#374151', marginBottom: '0.5rem', display: 'block' }}>Content</label>
              <div style={{ minHeight: '400px' }}>
                <ReactQuill
                  ref={quillRef}
                  theme="snow"
                  value={form.content}
                  onChange={val => setForm(f => ({ ...f, content: val }))}
                  modules={modules}
                  formats={formats}
                  style={{ height: '350px', marginBottom: '3rem' }}
                  placeholder="Write your blog post here... Use the toolbar to add images, videos, and format text."
                />
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div>
            {/* Status */}
            <div className="card" style={{ padding: '1.25rem', marginBottom: '1rem' }}>
              <label style={{ fontWeight: '600', fontSize: '0.9rem', color: '#374151', marginBottom: '0.5rem', display: 'block' }}>Status</label>
              <select
                value={form.status}
                onChange={e => setForm(f => ({ ...f, status: e.target.value }))}
                style={{ width: '100%', padding: '0.5rem', borderRadius: '6px', border: '1px solid #d1d5db' }}
              >
                <option value="draft">Draft</option>
                <option value="published">Published</option>
              </select>
            </div>

            {/* Featured Image */}
            <div className="card" style={{ padding: '1.25rem', marginBottom: '1rem' }}>
              <label style={{ fontWeight: '600', fontSize: '0.9rem', color: '#374151', marginBottom: '0.5rem', display: 'block' }}>Featured Image</label>
              {form.featured_image ? (
                <div style={{ marginBottom: '0.75rem' }}>
                  <img src={form.featured_image} alt="Featured" style={{ width: '100%', borderRadius: '8px', objectFit: 'cover', maxHeight: '180px' }} />
                  <button className="btn btn-outline" style={{ marginTop: '0.5rem', width: '100%', fontSize: '0.85rem' }} onClick={() => setForm(f => ({ ...f, featured_image: '' }))}>Remove</button>
                </div>
              ) : (
                <label style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '2rem', border: '2px dashed #d1d5db', borderRadius: '8px', cursor: 'pointer', color: '#6b7280', textAlign: 'center' }}>
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"/></svg>
                  <span style={{ marginTop: '0.5rem', fontSize: '0.85rem' }}>Click to upload</span>
                  <input type="file" accept="image/*" onChange={handleFeaturedImageUpload} hidden />
                </label>
              )}
            </div>

            {/* Category & Read Time */}
            <div className="card" style={{ padding: '1.25rem', marginBottom: '1rem' }}>
              <label style={{ fontWeight: '600', fontSize: '0.9rem', color: '#374151', marginBottom: '0.5rem', display: 'block' }}>Category</label>
              <input
                type="text"
                placeholder="e.g. Thought Leadership"
                value={form.category}
                onChange={e => setForm(f => ({ ...f, category: e.target.value }))}
                style={{ width: '100%', padding: '0.5rem', borderRadius: '6px', border: '1px solid #d1d5db', marginBottom: '1rem' }}
              />
              <label style={{ fontWeight: '600', fontSize: '0.9rem', color: '#374151', marginBottom: '0.5rem', display: 'block' }}>Read Time</label>
              <input
                type="text"
                placeholder="e.g. 8 min read"
                value={form.read_time}
                onChange={e => setForm(f => ({ ...f, read_time: e.target.value }))}
                style={{ width: '100%', padding: '0.5rem', borderRadius: '6px', border: '1px solid #d1d5db' }}
              />
            </div>

            {/* Keywords */}
            <div className="card" style={{ padding: '1.25rem' }}>
              <label style={{ fontWeight: '600', fontSize: '0.9rem', color: '#374151', marginBottom: '0.5rem', display: 'block' }}>Keywords</label>
              <div style={{ display: 'flex', gap: '0.25rem', marginBottom: '0.5rem' }}>
                <input
                  type="text"
                  placeholder="Add keyword"
                  value={keywordInput}
                  onChange={e => setKeywordInput(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addKeyword())}
                  style={{ flex: 1, padding: '0.4rem', borderRadius: '6px', border: '1px solid #d1d5db', fontSize: '0.85rem' }}
                />
                <button className="btn btn-primary" style={{ padding: '0.4rem 0.75rem', fontSize: '0.85rem' }} onClick={addKeyword}>+</button>
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.25rem' }}>
                {form.keywords.map(kw => (
                  <span key={kw} style={{ background: '#EEF2FF', color: '#4F46E5', padding: '0.2rem 0.5rem', borderRadius: '4px', fontSize: '0.8rem', display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                    {kw}
                    <button onClick={() => removeKeyword(kw)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#4F46E5', fontWeight: 'bold', fontSize: '0.9rem', lineHeight: 1 }}>&times;</button>
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // List view
  return (
    <div className="page-container">
      <div className="page-header">
        <div>
          <h1>Blog Posts</h1>
          <p className="page-subtitle">{posts.length} post{posts.length !== 1 ? 's' : ''}</p>
        </div>
        <button className="btn btn-primary" onClick={() => openEditor()}>
          <svg width="16" height="16" viewBox="0 0 20 20" fill="currentColor" style={{ marginRight: '0.25rem' }}>
            <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
          </svg>
          New Post
        </button>
      </div>

      <div className="card">
        <div className="table-responsive">
          <table className="data-table">
            <thead>
              <tr>
                <th>Title</th>
                <th>Category</th>
                <th>Status</th>
                <th>Created</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {posts.length === 0 ? (
                <tr><td colSpan="5" style={{ textAlign: 'center', padding: '3rem', color: '#9ca3af' }}>No blog posts yet. Create your first post!</td></tr>
              ) : posts.map(post => (
                <tr key={post.id}>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                      {post.featured_image && (
                        <img src={post.featured_image} alt="" style={{ width: '48px', height: '36px', objectFit: 'cover', borderRadius: '4px' }} />
                      )}
                      <div>
                        <div style={{ fontWeight: '600', color: '#111827' }}>{post.title}</div>
                        <div style={{ fontSize: '0.8rem', color: '#9ca3af' }}>/blog/{post.slug}</div>
                      </div>
                    </div>
                  </td>
                  <td><span className="badge" style={{ background: '#EEF2FF', color: '#4F46E5' }}>{post.category || '-'}</span></td>
                  <td>
                    <span className={`badge ${post.status === 'published' ? 'badge-success' : 'badge-warning'}`}>
                      {post.status === 'published' ? 'Published' : 'Draft'}
                    </span>
                  </td>
                  <td>{format(new Date(post.created_at), 'MMM d, yyyy')}</td>
                  <td>
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      <button className="btn btn-outline btn-sm" onClick={() => openEditor(post)}>Edit</button>
                      <button className="btn btn-outline btn-sm" style={{ color: '#ef4444', borderColor: '#fecaca' }} onClick={() => handleDelete(post.id)}>Delete</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default BlogPosts;
